import { AsyncPipe, DatePipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { VideoLibraryFilterParams } from '@app/instructor/course-curriculum/course-curriculum.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AngularSvgIconModule } from 'angular-svg-icon';
import * as _ from 'lodash';
import { Observable, distinctUntilChanged, filter, firstValueFrom, map, of, startWith, take } from 'rxjs';
import {
  AnnotationDetail,
  ContentTypeModels,
  CourseActions,
  CourseActionsV2,
  CourseService,
  RouterStoreService,
  TruncatePipe,
  isDefined,
  selectCourseCurriculumV2,
  selectSelectedAnnotation,
} from 'thkee-common';

type FeedbackType = 'danger' | 'warning' | 'normal';

@UntilDestroy()
@Component({
  selector: 'app-common-feedback',
  templateUrl: './feedback.component.html',
  styleUrls: ['./feedback.component.scss'],
  standalone: true,
  imports: [NgIf, NgTemplateOutlet, AngularSvgIconModule, NgClass, AsyncPipe, TruncatePipe, DatePipe],
})
export class FeedbackComponent implements OnInit {
  // NOTE: will pull data from state if this is not supplied
  @Input() annotation?: AnnotationDetail;
  @Output() filterEvent = new EventEmitter<boolean>();

  annotation$!: Observable<AnnotationDetail>;
  feedbackType$!: Observable<FeedbackType>;
  courseId: string = '';
  private courseId$ = this.routerStore.getParam('courseId');
  curriculumQueryParams: VideoLibraryFilterParams = { page: 1, page_size: 10 };

  constructor(
    private store: Store,
    private router: Router,
    private routerStore: RouterStoreService,
    private coursesService: CourseService
  ) {}

  ngOnInit(): void {
    if (!this.annotation) {
      this.annotation$ = this.store.select(selectSelectedAnnotation);
    } else {
      this.annotation$ = of(this.annotation);
    }

    this.courseId$.pipe(untilDestroyed(this)).subscribe((courseId) => {
      this.courseId = courseId;
    });

    this.feedbackType$ = this.annotation$.pipe(
      filter(isDefined),
      map((annotation) => (annotation.required_action ? 'danger' : 'warning')),
      distinctUntilChanged(),
      startWith('normal' as FeedbackType)
    );
  }

  formatTitle(fieldKey: string): string {
    return _.startCase(fieldKey.replace(/_/g, ' ').toLowerCase());
  }

  onResolve() {
    this.annotation$.pipe(take(1)).subscribe((annotation) => {
      this.store.dispatch(CourseActions.resolveFeedback({ annotation }));
    });
  }

  onUnresolve() {
    this.annotation$.pipe(take(1)).subscribe((annotation) => {
      this.store.dispatch(CourseActions.unresolveFeedback({ annotation }));
    });
  }

  gotoFeedback(annotation: any) {
    console.log('annotation--', this.courseId, annotation);
    const modelMappings: Record<ContentTypeModels, string> = {
      [ContentTypeModels.COURSE]: 'details',
      [ContentTypeModels.OBJECTIVE]: 'intended-learners',
      [ContentTypeModels.LEARNER]: 'intended-learners',
      [ContentTypeModels.REQUIREMENT]: 'intended-learners',
      [ContentTypeModels.SECTION]: 'curriculum',
      [ContentTypeModels.SUBSECTION]: 'curriculum',
      [ContentTypeModels.QUIZ_QUESTION]: 'curriculum',
      [ContentTypeModels.ASSIGNMENT_QUESTION]: 'curriculum',
      [ContentTypeModels.LECTURE]: 'curriculum',
      [ContentTypeModels.QUIZ]: 'curriculum',
      [ContentTypeModels.ASSIGNMENT]: 'curriculum',
    };
    const model = modelMappings[annotation.model as keyof typeof modelMappings] || 'details';
    if (
      annotation.model === 'section' ||
      annotation.model === 'lecture' ||
      annotation.model === 'quiz' ||
      annotation.model === 'assignment' ||
      annotation.model === 'subsection' ||
      annotation.model === 'quizquestion' ||
      annotation.model === 'assignmentquestion'
    ) {
      this.gotoCourseCurriculum(annotation, model);
    } else {
      if (annotation && this.courseId) {
        if (annotation.model === 'course') {
          this.router.navigate(['/instructor/course', this.courseId, model], {
            fragment: `feedback-${annotation.field}`,
          });
        } else {
          this.router.navigate(['/instructor/course', this.courseId, model], {
            fragment: `feedback-${annotation.object_id}`,
          });
        }
      }
    }
  }

  async gotoCourseCurriculum(annotation: any, model: string) {
    const curriculum = await firstValueFrom(this.store.select(selectCourseCurriculumV2));
    const isMatchFound = curriculum.results.sections.some((section) =>
      this.searchObjectForMatch(section, 'source', annotation.object_id)
    );
    if (isMatchFound) {
      this.filterEvent.emit(false);
      this.router.navigate(['/instructor/course', this.courseId, model], {
        fragment: `feedback-${annotation.object_id}`,
      });
    } else {
      this.loadMoreCurriculum(annotation, model);
    }
  }

  searchObjectForMatch(obj: any, matchKey: string, matchValue: any): boolean {
    if (typeof obj !== 'object' || obj === null) {
      return false;
    }
    if (obj[matchKey] === matchValue) {
      return true;
    }
    return Object.values(obj).some(
      (value) =>
        Array.isArray(value)
          ? value.some((item) => this.searchObjectForMatch(item, matchKey, matchValue)) // Recursively check arrays
          : this.searchObjectForMatch(value, matchKey, matchValue) // Recursively check objects
    );
  }

  loadMoreCurriculum(annotation: any, model: string) {
    this.filterEvent.emit(true);
    firstValueFrom(this.routerStore.getParam('courseId')).then((courseId) => {
      this.curriculumQueryParams.page += 1;
      this.coursesService.getInstructorCourseCurriculum(courseId, this.curriculumQueryParams).subscribe({
        next: (data) => {
          this.store.dispatch(CourseActionsV2.autoLoadCourseCurriculumSuccess(data));
          setTimeout(() => {
            this.gotoCourseCurriculum(annotation, model);
          }, 1000);
        },
        error: (error) => {
          this.store.dispatch(CourseActionsV2.autoLoadCourseCurriculumFail({ error }));
        },
      });
    });
  }
}
